import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer
} from 'recharts';
import PageContainer from '../components/PageContainer';

// Mock data for charts - extracted as constants for clarity
const monthlyRevenueData = [
  { month: 'Jan', revenue: 4500, target: 5000 },
  { month: 'Feb', revenue: 5200, target: 5000 },
  { month: 'Mar', revenue: 4800, target: 5000 },
  { month: 'Apr', revenue: 6100, target: 5000 },
  { month: 'May', revenue: 5500, target: 5000 },
  { month: 'Jun', revenue: 6700, target: 5000 },
];

const trendData = [
  { month: 'Jan', users: 1200, engagement: 78 },
  { month: 'Feb', users: 1350, engagement: 82 },
  { month: 'Mar', users: 1180, engagement: 75 },
  { month: 'Apr', users: 1420, engagement: 85 },
  { month: 'May', users: 1380, engagement: 88 },
  { month: 'Jun', users: 1650, engagement: 92 },
];

/**
 * RechartsTestPage
 *
 * Temporary test page for validating Recharts functionality before using it in production features.
 *
 * This page demonstrates:
 * - Bar chart with monthly revenue data
 * - Line chart with user growth trends
 * - Responsive design using ResponsiveContainer
 * - Chart customization with colors, legends, and tooltips
 *
 * Recharts is this project's charting library. Official documentation: https://recharts.org/
 */
const RechartsTestPage: React.FC = () => {
  return (
    <PageContainer maxWidth="wide">
      <div style={{ padding: '2rem 0' }}>
        {/* Warning Banner */}
        <div style={{
          backgroundColor: 'var(--danger)',
          color: 'white',
          padding: '1rem',
          borderRadius: 'var(--radius-md)',
          marginBottom: '2rem',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          ⚠️ This is a temporary test page and will be deleted after validation
        </div>

        {/* Page Title */}
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: '2rem',
          color: 'var(--text)'
        }}>
          Recharts Test Page (Temporary)
        </h1>

        {/* Charts Container */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>

          {/* Bar Chart Section */}
          <div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
              color: 'var(--text)',
              textAlign: 'center'
            }}>
              Monthly Revenue Performance
            </h2>
            <div style={{
              backgroundColor: 'var(--surface)',
              border: `1px solid var(--border)`,
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              boxShadow: 'var(--shadow-md)'
            }}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={monthlyRevenueData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="month"
                    stroke="var(--text-muted)"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="var(--text-muted)"
                    fontSize={12}
                    label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--surface)',
                      border: `1px solid var(--border)`,
                      borderRadius: 'var(--radius-sm)',
                      boxShadow: 'var(--shadow-md)'
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="revenue"
                    fill="#28a745"
                    name="Actual Revenue"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="target"
                    fill="#b6f5c6"
                    name="Target Revenue"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Line Chart Section */}
          <div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
              color: 'var(--text)',
              textAlign: 'center'
            }}>
              User Growth & Engagement Trends
            </h2>
            <div style={{
              backgroundColor: 'var(--surface)',
              border: `1px solid var(--border)`,
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              boxShadow: 'var(--shadow-md)'
            }}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={trendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="month"
                    stroke="var(--text-muted)"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="var(--text-muted)"
                    fontSize={12}
                    yAxisId="left"
                    label={{ value: 'Users', angle: -90, position: 'insideLeft' }}
                  />
                  <YAxis
                    stroke="var(--text-muted)"
                    fontSize={12}
                    yAxisId="right"
                    orientation="right"
                    label={{ value: 'Engagement (%)', angle: 90, position: 'insideRight' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--surface)',
                      border: `1px solid var(--border)`,
                      borderRadius: 'var(--radius-sm)',
                      boxShadow: 'var(--shadow-md)'
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="users"
                    stroke="#059669"
                    strokeWidth={3}
                    name="Active Users"
                    dot={{ fill: '#059669', strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="engagement"
                    stroke="#dc2626"
                    strokeWidth={3}
                    name="Engagement Rate (%)"
                    dot={{ fill: '#dc2626', strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>

        {/* Documentation Note */}
        <div style={{
          marginTop: '3rem',
          padding: '1.5rem',
          backgroundColor: 'var(--surface-alt)',
          border: `1px solid var(--border)`,
          borderRadius: 'var(--radius-md)',
          textAlign: 'center'
        }}>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            <strong>Recharts Documentation:</strong>{' '}
            <a
              href="https://recharts.org/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--brand)', textDecoration: 'underline' }}
            >
              https://recharts.org/
            </a>
          </p>
        </div>
      </div>
    </PageContainer>
  );
};

export default RechartsTestPage;
